import React, { useEffect, useState } from 'react'
import AdminCooperativeUsers from '../AdminCooperativeUsers'
import { useTranslation } from '@structura/i18n'

interface AdminUser {
  id: string
  email: string
  name?: string
  tier: string
  createdAt: string
  lastLoginAt?: string
  hasLocalPassword: boolean
  isAdmin: boolean
  isActive: boolean
  // Company Profile
  portalId?: string
  companyName?: string
  contactEmail?: string
  companyPitch?: string
  hqLocation?: string
  sectorFocus?: string
  serviceKeywords?: string
  professionalCertifications?: string
  role?: string
}

interface PasswordReset {
  id: number
  email: string
  token: string
  createdAt: string
  expiresAt: string
}

export interface UsersSectionProps {
  getSessionHeaders: () => HeadersInit
}

const UsersSection: React.FC<UsersSectionProps> = ({ getSessionHeaders }) => {
  const { t } = useTranslation()
  const [usersLoading, setUsersLoading] = useState<boolean>(false)
  const [usersError, setUsersError] = useState<string | null>(null)
  const [users, setUsers] = useState<AdminUser[]>([])
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [userEmail, setUserEmail] = useState<string>('')
  const [userName, setUserName] = useState<string>('')
  const [userTier, setUserTier] = useState<string>('Free')
  const [userPassword, setUserPassword] = useState<string>('')
  const [userIsAdmin, setUserIsAdmin] = useState<boolean>(false)
  const [userIsActive, setUserIsActive] = useState<boolean>(false)
  const [userPortalId, setUserPortalId] = useState<string>('')
  const [userCompanyName, setUserCompanyName] = useState<string>('')
  const [userContactEmail, setUserContactEmail] = useState<string>('')
  const [userCompanyPitch, setUserCompanyPitch] = useState<string>('')
  const [userHqLocation, setUserHqLocation] = useState<string>('')
  const [userSectorFocus, setUserSectorFocus] = useState<string>('')
  const [userServiceKeywords, setUserServiceKeywords] = useState<string>('')
  const [userProfessionalCertifications, setUserProfessionalCertifications] = useState<string>('')
  const [userRole, setUserRole] = useState<string>('Designer')

  const [passwordResets, setPasswordResets] = useState<PasswordReset[]>([])
  const [, setPasswordResetsLoading] = useState<boolean>(false)

  const loadUsers = async (): Promise<void> => {
    setUsersLoading(true)
    setUsersError(null)
    try {
      const response = await fetch('/api/AdminUsers', {
        headers: getSessionHeaders()
      })

      if (!response.ok) {
        throw new Error(t('admin.users.errLoadHttp', { status: response.status }))
      }

      const data: AdminUser[] = await response.json()
      setUsers(data)
    } catch (err) {
      const message = (err as Error).message || t('admin.users.errLoad')
      setUsersError(message)
    } finally {
      setUsersLoading(false)
    }
  }

  const loadPasswordResets = async (): Promise<void> => {
    setPasswordResetsLoading(true)
    try {
      const response = await fetch('/api/AdminUsers/password-resets', {
        headers: getSessionHeaders()
      })
      if (response.ok) {
        const data: PasswordReset[] = await response.json()
        setPasswordResets(data)
      }
    } catch {
      // Silently ignore errors
    } finally {
      setPasswordResetsLoading(false)
    }
  }

  useEffect(() => {
    void loadUsers()
    void loadPasswordResets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const copyResetLink = (token: string): void => {
    const baseUrl = window.location.origin
    const resetUrl = `${baseUrl}/reset-password?token=${token}`
    navigator.clipboard.writeText(resetUrl)
    alert(t('admin.users.resetLinkCopied'))
  }

  const invalidateResetToken = async (id: number): Promise<void> => {
    try {
      const response = await fetch(`/api/AdminUsers/password-resets/${id}`, {
        method: 'DELETE',
        headers: getSessionHeaders()
      })
      if (response.ok) {
        setPasswordResets(prev => prev.filter(r => r.id !== id))
      }
    } catch {
      // Silently ignore errors
    }
  }

  const resetUserForm = (): void => {
    setEditingUserId(null)
    setUserEmail('')
    setUserName('')
    setUserTier('Free')
    setUserPassword('')
    setUserIsAdmin(false)
    setUserIsActive(false)
    setUserPortalId('')
    setUserCompanyName('')
    setUserContactEmail('')
    setUserCompanyPitch('')
    setUserHqLocation('')
    setUserSectorFocus('')
    setUserServiceKeywords('')
    setUserProfessionalCertifications('')
    setUserRole('Designer')
  }

  const startCreateUser = (): void => {
    resetUserForm()
  }

  const startEditUser = (user: AdminUser): void => {
    setEditingUserId(user.id)
    setUserEmail(user.email)
    setUserName(user.name ?? '')
    setUserTier(user.tier)
    setUserPassword('')
    setUserIsAdmin(user.isAdmin)
    setUserIsActive(user.isActive)
    setUserPortalId(user.portalId ?? '')
    setUserCompanyName(user.companyName ?? '')
    setUserContactEmail(user.contactEmail ?? '')
    setUserCompanyPitch(user.companyPitch ?? '')
    setUserHqLocation(user.hqLocation ?? '')
    setUserSectorFocus(user.sectorFocus ?? '')
    setUserServiceKeywords(user.serviceKeywords ?? '')
    setUserProfessionalCertifications(user.professionalCertifications ?? '')
    setUserRole(user.role ?? 'Designer')
  }

  const saveUser = async (): Promise<void> => {
    setUsersError(null)
    const payload = {
      email: userEmail,
      name: userName.length > 0 ? userName : undefined,
      tier: userTier,
      password: userPassword.length > 0 ? userPassword : undefined,
      isAdmin: userIsAdmin,
      isActive: userIsActive,
      portalId: userPortalId.length > 0 ? userPortalId : undefined,
      companyName: userCompanyName.length > 0 ? userCompanyName : undefined,
      contactEmail: userContactEmail.length > 0 ? userContactEmail : undefined,
      companyPitch: userCompanyPitch.length > 0 ? userCompanyPitch : undefined,
      hqLocation: userHqLocation.length > 0 ? userHqLocation : undefined,
      sectorFocus: userSectorFocus.length > 0 ? userSectorFocus : undefined,
      serviceKeywords: userServiceKeywords.length > 0 ? userServiceKeywords : undefined,
      professionalCertifications: userProfessionalCertifications.length > 0 ? userProfessionalCertifications : undefined,
      role: userRole
    }

    try {
      const method: string = editingUserId === null ? 'POST' : 'PUT'
      const url: string = editingUserId === null ? '/api/AdminUsers' : `/api/AdminUsers/${editingUserId}`

      const response = await fetch(url, {
        method,
        headers: {
          ...getSessionHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      const text = await response.text()
      if (!response.ok) {
        let message = t('admin.users.errSaveHttp', { status: response.status })
        try {
          const parsed = JSON.parse(text)
          if (parsed !== null && typeof parsed.error === 'string') {
            message = parsed.error
          }
        } catch {
          // ignore JSON parse error
        }
        throw new Error(message)
      }

      const updatedUser: AdminUser = JSON.parse(text)

      setUsers(prev => {
        const copy = [...prev]
        const index = copy.findIndex(candidate => candidate.id === updatedUser.id)
        if (index >= 0) {
          copy[index] = updatedUser
        } else {
          copy.unshift(updatedUser)
        }
        return copy
      })

      resetUserForm()
    } catch (err) {
      const message = (err as Error).message || t('admin.users.errSave')
      setUsersError(message)
    }
  }

  const deleteUser = async (user: AdminUser): Promise<void> => {
    if (!window.confirm(t('admin.users.confirmDelete', { email: user.email }))) {
      return
    }

    try {
      const response = await fetch(`/api/AdminUsers/${user.id}`, {
        method: 'DELETE',
        headers: getSessionHeaders()
      })

      if (!response.ok) {
        const text = await response.text()
        throw new Error(t('admin.users.errDeleteHttp', { status: response.status, text }))
      }

      setUsers(prev => prev.filter(candidate => candidate.id !== user.id))
      if (editingUserId === user.id) {
        resetUserForm()
      }
    } catch (err) {
      const message = (err as Error).message || t('admin.users.errDelete')
      setUsersError(message)
    }
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="bi bi-people me-2"></i>
          {t('admin.menu.users')}
        </h5>
        <button type="button" className="btn btn-sm btn-primary" onClick={startCreateUser}>
          <i className="bi bi-person-plus me-1"></i>
          {t('admin.users.newUser')}
        </button>
      </div>
      <div className="card-body">
        <p className="text-muted">
          {t('admin.users.introBefore')} <code>Users.db</code>{t('admin.users.introAfter')}
        </p>

        {usersLoading && (
          <div className="d-flex align-items-center mb-3">
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <span>{t('admin.users.loading')}</span>
          </div>
        )}

        {usersError && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {usersError}
            <button type="button" className="btn-close" onClick={() => setUsersError(null)}></button>
          </div>
        )}

        <div className="row">
          <div className="col-lg-7">
            <div className="table-responsive mb-3">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{t('admin.users.thEmail')}</th>
                    <th>{t('admin.users.thName')}</th>
                    <th>{t('admin.users.thTier')}</th>
                    <th className="text-end">{t('admin.users.thCreated')}</th>
                    <th className="text-end">{t('admin.users.thLastLogin')}</th>
                    <th className="text-center">{t('admin.users.thPassword')}</th>
                    <th className="text-center">{t('admin.users.thAdmin')}</th>
                    <th className="text-center">{t('admin.users.thActive')}</th>
                    <th className="text-end">{t('admin.users.thActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && !usersLoading && (
                    <tr>
                      <td colSpan={7}>
                        <span className="text-muted">{t('admin.users.noUsers')}</span>
                      </td>
                    </tr>
                  )}
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.email}</strong>
                      </td>
                      <td>{user.name ?? <span className="text-muted">—</span>}</td>
                      <td>
                        <span className="badge bg-secondary-subtle text-secondary">{user.tier}</span>
                      </td>
                      <td className="text-end">
                        {new Date(user.createdAt).toLocaleDateString('en-GB', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="text-end">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleDateString('en-GB', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                          : <span className="text-muted">{t('admin.users.never')}</span>}
                      </td>
                      <td className="text-center">
                        {user.hasLocalPassword ? (
                          <span className="badge bg-success-subtle text-success">{t('admin.users.passwordSet')}</span>
                        ) : (
                          <span className="badge bg-secondary-subtle text-secondary">{t('admin.users.passwordNone')}</span>
                        )}
                      </td>
                      <td className="text-center">
                        {user.isAdmin ? (
                          <span className="badge bg-danger-subtle text-danger">{t('admin.users.roleBadgeAdmin')}</span>
                        ) : (
                          <span className="badge bg-secondary-subtle text-secondary">{t('admin.users.roleBadgeUser')}</span>
                        )}
                      </td>
                      <td className="text-center">
                        {user.isActive ? (
                          <span className="badge bg-success-subtle text-success">{t('admin.users.statusActive')}</span>
                        ) : (
                          <span className="badge bg-warning-subtle text-warning">{t('admin.users.statusPending')}</span>
                        )}
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => startEditUser(user)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => void deleteUser(user)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">
                  {editingUserId === null ? t('admin.users.createUser') : t('admin.users.editUser')}
                </h6>
              </div>
              <div className="card-body">
                <form
                  onSubmit={event => {
                    event.preventDefault()
                    void saveUser()
                  }}
                >
                  <div className="mb-3">
                    <label htmlFor="adminUserEmail" className="form-label">
                      {t('admin.users.email')}
                    </label>
                    <input
                      id="adminUserEmail"
                      type="email"
                      className="form-control"
                      value={userEmail}
                      onChange={event => setUserEmail(event.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="adminUserName" className="form-label">
                      {t('admin.users.nameOptional')}
                    </label>
                    <input
                      id="adminUserName"
                      type="text"
                      className="form-control"
                      value={userName}
                      onChange={event => setUserName(event.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('admin.users.portalId')}</label>
                    <input type="text" className="form-control" value={userPortalId} onChange={e => setUserPortalId(e.target.value)} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">{t('admin.users.companyName')}</label>
                    <input type="text" className="form-control" value={userCompanyName} onChange={e => setUserCompanyName(e.target.value)} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">{t('admin.users.contactEmail')}</label>
                    <input type="email" className="form-control" value={userContactEmail} onChange={e => setUserContactEmail(e.target.value)} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('admin.users.companyPitch')}</label>
                    <input type="text" className="form-control" value={userCompanyPitch} onChange={e => setUserCompanyPitch(e.target.value)} placeholder={t('admin.users.companyPitchPlaceholder')} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('admin.users.hqLocation')}</label>
                    <input type="text" className="form-control" value={userHqLocation} onChange={e => setUserHqLocation(e.target.value)} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('admin.users.sectorFocus')}</label>
                    <input type="text" className="form-control" value={userSectorFocus} onChange={e => setUserSectorFocus(e.target.value)} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('admin.users.serviceKeywords')}</label>
                    <input type="text" className="form-control" value={userServiceKeywords} onChange={e => setUserServiceKeywords(e.target.value)} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('admin.users.certifications')}</label>
                    <input type="text" className="form-control" value={userProfessionalCertifications} onChange={e => setUserProfessionalCertifications(e.target.value)} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('admin.users.role')}</label>
                    <select className="form-select" value={userRole} onChange={e => setUserRole(e.target.value)}>
                      <option value="Designer">{t('admin.users.roleDesigner')}</option>
                      <option value="Approval">{t('admin.users.roleApproval')}</option>
                      <option value="Info">{t('admin.users.roleInfo')}</option>
                      <option value="All">{t('admin.users.roleAll')}</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="adminUserTier" className="form-label">
                      {t('admin.users.subscriptionTier')}
                    </label>
                    <select
                      id="adminUserTier"
                      className="form-select"
                      value={userTier}
                      onChange={event => setUserTier(event.target.value)}
                    >
                      <option value="Free">{t('admin.users.tierFree')}</option>
                      <option value="Professional">{t('admin.users.tierProfessional')}</option>
                      <option value="Enterprise">{t('admin.users.tierEnterprise')}</option>
                    </select>
                  </div>

                  <div className="form-check mb-3">
                    <input
                      id="adminUserIsAdmin"
                      type="checkbox"
                      className="form-check-input"
                      checked={userIsAdmin}
                      onChange={event => setUserIsAdmin(event.target.checked)}
                    />
                    <label htmlFor="adminUserIsAdmin" className="form-check-label">
                      {t('admin.users.grantAdmin')}
                    </label>
                  </div>

                  <div className="form-check mb-3">
                    <input
                      id="adminUserIsActive"
                      type="checkbox"
                      className="form-check-input"
                      checked={userIsActive}
                      onChange={event => setUserIsActive(event.target.checked)}
                    />
                    <label htmlFor="adminUserIsActive" className="form-check-label text-success">
                      <strong>{t('admin.users.activateStrong')}</strong>{t('admin.users.activateRest')}
                    </label>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="adminUserPassword" className="form-label">
                      {t('admin.users.password')} {editingUserId === null ? '' : t('admin.users.passwordKeepCurrent')}
                    </label>
                    <input
                      id="adminUserPassword"
                      type="password"
                      className="form-control"
                      value={userPassword}
                      onChange={event => setUserPassword(event.target.value)}
                    />
                    <div className="form-text">
                      {t('admin.users.passwordHelp')}
                    </div>
                  </div>

                  <div className="mb-3">
                    <AdminCooperativeUsers userId={editingUserId} getHeaders={getSessionHeaders} />
                  </div>

                  <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-outline-secondary me-2" onClick={resetUserForm}>
                      {t('admin.users.clear')}
                    </button>
                    <button type="submit" className="btn btn-primary">
                      <i className="bi bi-save me-1"></i>
                      {t('admin.users.save')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Password Reset Requests */}
        {passwordResets.length > 0 && (
          <div className="mt-4">
            <div className="card border-warning">
              <div className="card-header bg-warning-subtle">
                <h6 className="mb-0">
                  <i className="bi bi-key me-2"></i>
                  {t('admin.users.pendingResets', { count: passwordResets.length })}
                </h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('admin.users.thEmail')}</th>
                        <th>{t('admin.users.thRequested')}</th>
                        <th>{t('admin.users.thExpires')}</th>
                        <th className="text-end">{t('admin.users.thActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {passwordResets.map(reset => (
                        <tr key={reset.id}>
                          <td>{reset.email}</td>
                          <td>{new Date(reset.createdAt).toLocaleString('en-GB')}</td>
                          <td>{new Date(reset.expiresAt).toLocaleString('en-GB')}</td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => copyResetLink(reset.token)}
                              title={t('admin.users.copyLinkTitle')}
                            >
                              <i className="bi bi-clipboard"></i> {t('admin.users.copyLink')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => void invalidateResetToken(reset.id)}
                              title={t('admin.users.invalidateTitle')}
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default UsersSection
